<template>
    <div class="flex flex-col gap-4">
        <page-header>
            Sessions

            <template v-slot:actions>
                <vx-button
                    color="danger"
                    outline
                    @click.prevent="signOutEverywhereElse"
                    :disabled="!others"
                    :loading="submitting"
                    data-test="sign-out-everywhere-else"
                >
                    Sign out everywhere else
                    <font-awesome-icon widthAuto :icon="faRightFromBracket"></font-awesome-icon>
                </vx-button>
            </template>
        </page-header>

        <vx-alert v-if="success" :closeable="false" color="success" shadow> {{ success }} </vx-alert>

        <vx-expandable-card>
            <template v-slot:header>Web Sessions</template>
            <template v-slot:content>
                <page-loading v-if="loading && !sessions.length"></page-loading>

                <vx-table v-else-if="sessions.length" class="xl:grid-cols-4" rowBreakpoint="xl">
                    <vx-table-row isHeader>
                        <div class="lg:pl-6">Browser</div>
                        <div>IP Address</div>
                        <div>Last Seen</div>
                        <div></div>
                    </vx-table-row>

                    <vx-table-row v-for="session in sessions" :key="session.id" data-test="session">
                        <vx-table-row-title>
                            {{ userAgentLabel(session.user_agent) }}
                        </vx-table-row-title>

                        <vx-table-row-item>
                            <template v-slot:label> IP Address: </template>
                            <vx-popover v-if="session.ip_address" hover arrow placement="top" class="w-full">
                                <button
                                    type="button"
                                    class="block max-w-full text-left focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                                    data-test="ip-address"
                                >
                                    <span class="block truncate">{{ session.ip_address }}</span>
                                </button>
                                <template v-slot:content>
                                    <code class="text-xs">{{ session.ip_address }}</code>
                                </template>
                            </vx-popover>
                            <template v-else>-</template>
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <template v-slot:label> Last Seen: </template>
                            {{ shortDateTime(session.last_seen_at) }}
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <span v-if="session.current" class="text-sm text-slate-500" data-test="current"
                                >This session</span
                            >
                            <vx-button v-else size="sm" color="muted" outline @click="end(session)" data-test="end">
                                End <font-awesome-icon widthAuto :icon="faRightFromBracket"></font-awesome-icon>
                            </vx-button>
                        </vx-table-row-item>
                    </vx-table-row>
                </vx-table>

                <div v-else class="text-sm text-slate-500" data-test="no-sessions">No web sessions.</div>
            </template>
        </vx-expandable-card>

        <vx-expandable-card>
            <template v-slot:header>Mobile Devices</template>
            <template v-slot:content>
                <page-loading v-if="loading && !devices.length"></page-loading>

                <vx-table v-else-if="devices.length" class="xl:grid-cols-5" rowBreakpoint="xl">
                    <vx-table-row isHeader>
                        <div class="lg:pl-6">Device</div>
                        <div>Registered</div>
                        <div>Last Used</div>
                        <div>Single Sign-On</div>
                        <div></div>
                    </vx-table-row>

                    <vx-table-row v-for="device in devices" :key="device.id" data-test="device">
                        <vx-table-row-title>
                            {{ device.name }} · {{ deviceTypeLabel(device.type) }}
                        </vx-table-row-title>

                        <vx-table-row-item>
                            <template v-slot:label> Registered: </template>
                            {{ shortDateTime(device.created_at) }}
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <template v-slot:label> Last Used: </template>
                            {{ shortDateTime(device.updated_at) }}
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <template v-slot:label> Single Sign-On: </template>
                            {{ ssoLabel(device) }}
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <vx-button
                                size="sm"
                                color="muted"
                                outline
                                @click="endDevice(device)"
                                data-test="end-device"
                            >
                                End <font-awesome-icon widthAuto :icon="faRightFromBracket"></font-awesome-icon>
                            </vx-button>
                        </vx-table-row-item>
                    </vx-table-row>
                </vx-table>

                <div v-else class="text-sm text-slate-500" data-test="no-devices">No mobile devices.</div>
            </template>
        </vx-expandable-card>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import axios from '~/services/axios';
import { faRightFromBracket } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxButton,
    VxExpandableCard,
    VxPopover,
    VxSwal,
    VxTable,
    VxTableRow,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { shortDateTime } from '~/utils/date';
import { throwSwalRequestError } from '~/utils/request';
import { userAgentLabel } from '~/utils/userAgent';

interface ProfileSession {
    id: number;
    user_id: number;
    impersonator_id: number | null;
    current_team_id: number | null;
    method: 'password' | 'sso' | 'impersonation';
    remembered: boolean;
    ip_address: string | null;
    user_agent: string | null;
    last_seen_at: string;
    ended_at: string | null;
    current: boolean;
    created_at: string;
    updated_at: string;
}

interface ProfileMobileDevice {
    id: string;
    type: 'android' | 'ios';
    name: string;
    current_team_id: number;
    sso_state: 'password' | 'entitled' | 'suspended' | 'expired';
    sso_entitled_until: string | null;
    created_at: string;
    updated_at: string;
}

const toasted = useToasted();
const loading = ref(true);
const submitting = ref(false);
const success = ref('');
const sessions = ref<ProfileSession[]>([]);
const devices = ref<ProfileMobileDevice[]>([]);

const others = computed(() => sessions.value.some((session) => !session.current) || devices.value.length > 0);

const load = async () => {
    loading.value = true;

    try {
        const { data } = await axios.get('/settings/sessions');
        sessions.value = data.sessions;
        devices.value = data.devices;
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
};

const deviceTypeLabel = (type: ProfileMobileDevice['type']) => (type === 'ios' ? 'iOS' : 'Android');

const ssoLabel = (device: ProfileMobileDevice) => {
    if (device.sso_state === 'password') {
        return 'Password';
    }

    if (device.sso_state === 'entitled') {
        return `Until ${shortDateTime(device.sso_entitled_until)}`;
    }

    return device.sso_state === 'suspended' ? 'Suspended' : 'Expired';
};

const end = (session: ProfileSession) => {
    VxSwal.confirmation(
        'End session',
        'That browser is signed out within a minute, or at its next visit if it is idle, and will need to log in again. There is no undo.',
        'danger',
        () =>
            axios
                .delete(`/settings/sessions/${session.id}`)
                .then(() => {
                    success.value = 'Session signed out';
                    load();
                })
                .catch(throwSwalRequestError)
    );
};

const endDevice = (device: ProfileMobileDevice) => {
    VxSwal.confirmation(
        'End device session',
        'The app on that phone is signed out when its access token next expires, within an hour, and will need to log in again. There is no undo.',
        'danger',
        () =>
            axios
                .delete(`/settings/mobile-devices/${device.id}`)
                .then(() => {
                    success.value = 'Device signed out';
                    load();
                })
                .catch(throwSwalRequestError)
    );
};

const signOutEverywhereElse = () => {
    VxSwal.confirmation(
        'Sign out everywhere else',
        'Every other browser is signed out within a minute, every phone when its access token next expires, and every browser that chose Remember me must log in again. This browser stays signed in.',
        'danger',
        () => {
            submitting.value = true;

            return axios
                .post('/settings/sessions/sign-out-everywhere-else')
                .then(() => {
                    success.value = 'Signed out everywhere else';
                    load();
                })
                .catch(throwSwalRequestError)
                .finally(() => {
                    submitting.value = false;
                });
        }
    );
};

load();
</script>
